import { useContext } from 'react';

import { OptimizelyDecideOption, useDecision } from '@optimizely/react-sdk';

import { UserContext } from 'Context/user';

import { FLAG } from 'Utilities/analytics/optimizely/constants';
import { isUndefined } from 'Utilities/helpers';
import { useFeatureFlags } from 'Utilities/hooks';

import { APIResponseStatus } from 'Services/api.types';

import { useSaleDetailsSub } from '../vehicleCollection/hooks/useSaleDetailsSub';
import { SELLER } from '../vehicleProfiling/VehicleProfiling.constants';

import { DocumentCaptureConfirmationProgressContext, DocumentCaptureOfferContext } from './DocumentCaptureContexts';

const DISABLE_DECISION = [OptimizelyDecideOption.DISABLE_DECISION_EVENT];

export const useDocumentCaptureExperimentDecisions = () => {
  const { incodeEnabled } = useFeatureFlags();
  const { userState: user } = useContext(UserContext);
  const { data } = useContext(DocumentCaptureOfferContext) || {};

  const {
    progress: {
      bankDetailsAdded,
      bankDetailsReviewFailed,
      documents,
    },
  } = useContext(DocumentCaptureConfirmationProgressContext) ?? {};

  const { hasNegativeEquity, offerStatus } = useSaleDetailsSub();

  const hasSomeDocs = documents?.some(({ uploaded }) => (!!uploaded));
  const isV5LogBookOwnedBySeller = data?.seller?.keeperType === SELLER;
  const hasValidBankDetails = bankDetailsAdded === true && !bankDetailsReviewFailed;
  const isOnFinance = data?.finance?.isVehicleOnFinance;
  const isDataLoaded = !isUndefined(data);

  const shouldFireDecision = isOnFinance && isDataLoaded;

  const [{ enabled, variationKey }, clientReady] = useDecision(
    FLAG.INCODE_SDK_FINANCE,
    { decideOptions: shouldFireDecision ? [] : DISABLE_DECISION },
    { overrideUserId: user?.dpSellerId?.toString() ?? '' },
  );

  const inFinanceExperiment = clientReady && enabled && variationKey === 'on' && isOnFinance;
  const isFinanceAllowedOrHasNoFinance = !isOnFinance || inFinanceExperiment;

  if (isUndefined(data) || isUndefined(documents) || offerStatus !== APIResponseStatus.SUCCESS) {
    return {
      isIncodeEligible: false,
      isMandatoryBankDetailsEligible: false,
    };
  }

  const isIncodeEligible = incodeEnabled
    && isV5LogBookOwnedBySeller
    && !hasSomeDocs
    && hasValidBankDetails
    && isFinanceAllowedOrHasNoFinance;

  const isMandatoryBankDetailsEligible = [
    !hasSomeDocs,
    !hasValidBankDetails,
    !hasNegativeEquity,
  ].every(Boolean);

  return {
    isIncodeEligible,
    isMandatoryBankDetailsEligible,
  };
};
